import type { ValidProperties } from '@wmgone/analytics';
import { createAnalyticsModule } from '@wmgone/analytics';
import { useCallback } from 'react';

export type AnalyticsAppType =
  | 'Artist Roster'
  | 'Label Performance'
  | 'Global Search'
  | 'Link Farm'
  | 'Digital Campaign Planner';

// Initialize the base Analytics module that will be used to track events in the
// various Container applications.
const analytics = createAnalyticsModule();

// Protect against multiple initializations of the Container analytics module.
let isContainerAnalyticsInitialized = false;

/**
 * Initializes the Container analytics module. This should be called once when
 * the Container is mounted. Note we use an exported funcion here so as not to
 * export the base analytics module itself.
 */
export function initContainerAnalytics() {
  // NOTE: Because we use React.StrictMode in the Container, in dev mode, this
  // function will actually always fire twice.
  if (isContainerAnalyticsInitialized) {
    return;
  }

  isContainerAnalyticsInitialized = true;

  // NOTE: Specifically not passing in an application name here. It will be set
  //       when using useAnalytics (e.g. - useAnalytics('Artist Roster')).
  analytics.init('');
}

/**
 * Custom hook that exposes what we need for the Container analytics.
 *
 * @param appType - The type of application that is using the analytics module.
 * @returns An object containing two functions to track events and flush analytics.
 */
export function useAnalytics(appType: AnalyticsAppType) {
  const trackEvent = useCallback(
    (eventName: string, eventProperties?: ValidProperties) => {
      const allProperties: ValidProperties = {
        ...eventProperties,
        application: appType,
      };

      analytics.trackEvent(eventName, allProperties);
    },
    [appType],
  );

  return {
    trackEvent,
    flushAnalytics: analytics.flushAnalytics,
  };
}
